import { ResponsiveLine } from "@nivo/line";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { FaChartLine } from "react-icons/fa";
import Loader from "../../components/Loader";
import { baseurl } from "../../config/url";

/**
 * ✅ SalesGraph — Professional UI/UX Upgrade (same logic)
 * - Clean header + KPI chips (Total, Avg/Month, Best Month)
 * - Range selector (Last 3/6/12 months)
 * - Better empty/error states
 * - Consistent dashboard styling (gray/white + blue)
 * - Nivo theme improved (no noisy grid, readable tooltip)
 */

const SalesGraph = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI State
  const [range, setRange] = useState("12m"); // 3m | 6m | 12m

  const fetchSalesData = async () => {
    const sellerId = localStorage.getItem("sellerId");
    const endDate = dayjs();
    const startDate = endDate.subtract(1, "year");

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${baseurl}/api/seller/monthly-sales/${sellerId}`,
        {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
        }
      );

      setSalesData(Array.isArray(response.data?.sales) ? response.data.sales : []);
    } catch (err) {
      console.error("Error fetching sales data:", err);
      setError("Failed to fetch sales data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, []);

  const sortedSales = useMemo(() => {
    // ensure correct chronological order (year, month)
    const arr = Array.isArray(salesData) ? [...salesData] : [];
    return arr.sort((a, b) => {
      const ad = dayjs().year(a.year).month(a.month - 1).date(1);
      const bd = dayjs().year(b.year).month(b.month - 1).date(1);
      return ad.valueOf() - bd.valueOf();
    });
  }, [salesData]);

  const rangeMonths = range === "3m" ? 3 : range === "6m" ? 6 : 12;

  const slicedSales = useMemo(() => {
    if (!sortedSales.length) return [];
    return sortedSales.slice(-rangeMonths);
  }, [sortedSales, rangeMonths]);

  const graphData = useMemo(
    () => [
      {
        id: "Sales",
        data: slicedSales.map((sale) => ({
          x: dayjs().month(sale.month - 1).year(sale.year).format("MMM YY"),
          y: Number(sale.totalSales || 0),
        })),
      },
    ],
    [slicedSales]
  );

  const totalSales = useMemo(
    () => slicedSales.reduce((t, s) => t + Number(s.totalSales || 0), 0),
    [slicedSales]
  );

  const avgMonthly = useMemo(() => {
    if (!slicedSales.length) return 0;
    return Math.round(totalSales / slicedSales.length);
  }, [totalSales, slicedSales]);

  const bestMonth = useMemo(() => {
    if (!slicedSales.length) return null;
    const best = slicedSales.reduce((max, cur) =>
      Number(cur.totalSales || 0) > Number(max.totalSales || 0) ? cur : max
    );
    return {
      label: dayjs().month(best.month - 1).year(best.year).format("MMM YY"),
      value: Number(best.totalSales || 0),
    };
  }, [slicedSales]);

  if (loading) {
    return (
      <div className="h-[420px] bg-gray-50 p-6 rounded-xl shadow-lg mb-10 flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[420px] bg-gray-50 p-6 rounded-xl shadow-lg mb-10 flex flex-col justify-center items-center text-center">
        <div className="text-red-600 font-semibold">{error}</div>
        <button
          onClick={fetchSalesData}
          className="mt-4 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 font-semibold hover:bg-gray-100 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!slicedSales.length) {
    return (
      <div className="h-[420px] bg-gray-50 p-6 rounded-xl shadow-lg mb-10 flex flex-col justify-center items-center text-center">
        <div className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-blue-600">
          <FaChartLine />
        </div>
        <div className="mt-4 text-gray-900 font-semibold">No sales data</div>
        <div className="mt-1 text-sm text-gray-600">
          Once you have monthly sales, analytics will appear here.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6 rounded-xl shadow-xl mb-10">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-blue-600">
            <FaChartLine />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Revenue Analytics
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Monthly sales trend for the selected period.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Range selector */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
            {[
              { key: "3m", label: "3M" },
              { key: "6m", label: "6M" },
              { key: "12m", label: "12M" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setRange(opt.key)}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                  range === opt.key
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <button
            onClick={fetchSalesData}
            className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 font-semibold hover:bg-gray-100 transition"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* KPI chips */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Total Sales
          </div>
          <div className="mt-1 text-xl font-bold text-gray-900">
            ₹{totalSales.toLocaleString()}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Avg / Month
          </div>
          <div className="mt-1 text-xl font-bold text-gray-900">
            ₹{avgMonthly.toLocaleString()}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Best Month
          </div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            {bestMonth?.label || "—"}
          </div>
          <div className="text-lg font-bold text-gray-900">
            ₹{(bestMonth?.value || 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-4 bg-white border border-gray-200 rounded-xl p-3 h-[320px]">
        <ResponsiveLine
          data={graphData}
          margin={{ top: 20, right: 20, bottom: 60, left: 55 }}
          xScale={{ type: "point" }}
          yScale={{
            type: "linear",
            min: 0,
            max: "auto",
            stacked: false,
            reverse: false,
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
            tickRotation: 0,
            legend: "",
            legendOffset: 36,
            legendPosition: "middle",
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 10,
            tickRotation: 0,
            legend: "",
            legendOffset: -45,
            legendPosition: "middle",
            format: (v) => `₹${Number(v).toLocaleString()}`,
          }}
          enableArea={true}
          areaOpacity={0.12}
          curve="monotoneX"
          colors={["#2563eb"]} // matches your dashboard blue
          lineWidth={3}
          pointSize={8}
          pointColor="#ffffff"
          pointBorderWidth={3}
          pointBorderColor={{ from: "serieColor" }}
          useMesh={true}
          enableSlices="x"
          gridYValues={5}
          tooltip={({ point }) => (
            <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm shadow-lg">
              <div className="font-semibold">{point.data.xFormatted}</div>
              <div className="opacity-90">
                ₹{Number(point.data.yFormatted).toLocaleString()}
              </div>
            </div>
          )}
          theme={{
            background: "transparent",
            axis: {
              domain: { line: { stroke: "#e5e7eb" } },
              ticks: { line: { stroke: "#e5e7eb" }, text: { fill: "#6b7280" } },
              legend: { text: { fill: "#6b7280" } },
            },
            grid: {
              line: {
                stroke: "#eef2f7",
                strokeWidth: 1,
              },
            },
            crosshair: {
              line: {
                stroke: "#94a3b8",
                strokeWidth: 1,
                strokeOpacity: 0.6,
              },
            },
          }}
        />
      </div>

      <div className="mt-3 text-xs text-gray-500">
        Tip: Use 3M / 6M / 12M to quickly compare short-term vs long-term trends.
      </div>
    </div>
  );
};

export default SalesGraph;